import { createSlice } from "@reduxjs/toolkit";
import { getTodayEndISO, getTodayStartISO } from "Utils";
import { deepCamelCaseKeys } from "Utils/collectionUtils";
import { sortMissionsByDeploymentDate } from "Components/LiveMissionsTable/liveMissionsUtils";

import { getSavedPeriod, getSavedVehiclesIds } from "./utils";
import { RowData } from "Components/NewList/types";

export const getDefaultPeriod = () => ({
  startDate: getTodayStartISO(),
  endDate: getTodayEndISO(),
});

type MissionPlanner = {
  isOrderMissions: boolean;
  currentFleetId: string | null;
  period: MissionPlannerPeriodFilter | null;
  searchValue: string;
  activeTab: MissionPlannerTabName;
  headerData: {
    name: string;
    version: string;
    assignedTo: string;
    status: string;
    stops: string;
    vdaActions?: string;
    loops: string;
  };
  limit: number;
  allMissionsCount: number;
  completedMissionsCount: number;
  deployedMissionsCount: number;
  // DEV-3710: bumped whenever a live WS frame changes the in-memory list's
  // membership (a mission added, or an existing mission's status changes). The
  // controller watches this token and fires a debounced `countersOnly` refetch
  // so the All/Deployed/Completed tab badges update live instead of only on a
  // tab click. The three counts are fleet-wide aggregates the client can't
  // recompute from the tab-filtered, paginated in-memory list, so the server
  // stays authoritative for them.
  countsRefreshToken: number;
  missionsQueue: null | Array<Mission>;
  selectedVehicleIds: Array<string>;
  isUpdating: boolean;
  isMissionsUpdatesPaused: null | boolean;
  missionsUpdatesStorage: Array<Mission | never>;
};

const initialState: MissionPlanner = {
  isOrderMissions: false,
  currentFleetId: null,
  period: null,
  searchValue: "",
  activeTab: "All",
  headerData: {
    name: "Mission Name",
    version: "Version",
    assignedTo: "Vehicle Assigned",
    status: "Status",
    stops: "Stops",
    vdaActions: "Actions",
    loops: "Cycles",
  },
  limit: 20,
  allMissionsCount: 0,
  completedMissionsCount: 0,
  deployedMissionsCount: 0,
  countsRefreshToken: 0,
  missionsQueue: null,
  selectedVehicleIds: [],
  isUpdating: false,
  isMissionsUpdatesPaused: false,
  missionsUpdatesStorage: [],
};

const cachedInitialState = {
  ...initialState,
  period: getSavedPeriod(),
  selectedVehicleIds: getSavedVehiclesIds(),
};

// DEV-3710: the mission-queue tabs are status-filtered server-side
// (cloud-core missions/filters.py): "Deployed" = status NOT IN FINAL_STATUSES,
// "Completed" = status IN FINAL_STATUSES, "All" = everything. Live WS frames
// must honor the active tab too — otherwise a live mission gets injected into
// the Completed list (or a just-finished mission lingers in Deployed) until the
// next refetch, which reads as "stale data" flux when switching tabs. Mirror
// FINAL_STATUSES here so the in-memory list matches the server's filtered set.
const FINAL_STATUSES: ReadonlyArray<MissionStatusCode> = [
  "FINISHED",
  "FAILURE",
  "CANCELED",
];

const matchesActiveTab = (
  status: MissionStatusCode | undefined,
  activeTab: MissionPlannerTabName
): boolean => {
  if (activeTab === "All") return true;
  const isFinal = !!status && FINAL_STATUSES.includes(status);
  return activeTab === "Completed" ? isFinal : !isFinal;
};

export const Slice = createSlice({
  name: "missionPlanner",
  initialState: cachedInitialState,
  reducers: {
    resetMissionPlannerData: () => initialState,
    setIsMissionPlannerUpdating: (state, action) => {
      state.isUpdating = action.payload;
      return state;
    },
    setCurrentFleetId: (state, action) => {
      state.currentFleetId = action.payload;
      return state;
    },
    setCurrentFleetPeriod: (state, action) => {
      state.period = action.payload;
      return state;
    },
    setMissionRowsLimit: (state, action) => {
      state.limit = action.payload;
      return state;
    },
    setCurrentSearchValue: (state, action) => {
      state.searchValue = action.payload;
      return state;
    },
    setHeaderData: (state, action) => {
      state.headerData = action.payload;
      return state;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      return state;
    },
    setMissionCounts: (state, action) => {
      state.allMissionsCount = action.payload.count;
      state.deployedMissionsCount = action.payload.deployedCount;
      state.completedMissionsCount = action.payload.completedCount;
      return state;
    },
    resetMissionsQueue: (state) => {
      state.missionsQueue = null;
      state.allMissionsCount = 0;
      state.completedMissionsCount = 0;
      state.deployedMissionsCount = 0;
      state.limit = 20;
      // countsRefreshToken is intentionally left as-is: it is a monotonic
      // change-signal, not a count. Resetting it to 0 here would either be a
      // no-op (already 0) or fire a spurious counts refetch; the next
      // membership-changing WS frame bumps it and repopulates the zeroed counts.
      return state;
    },
    setMissionsQueue: (state, action) => {
      state.missionsQueue = action.payload;
      return state;
    },
    reorderMissionsQueue: (state, action) => {
      const reorderedMissions = action.payload
        ?.map?.((id: RowData["id"]) => {
          const foundedReorderedItem = state.missionsQueue?.find(
            (item) => item.id === id
          );
          return foundedReorderedItem;
        })
        .filter(Boolean);

      state.missionsQueue = reorderedMissions
        ? reorderedMissions
        : state.missionsQueue;
      return state;
    },
    updateMissionsQueue: (state, action) => {
      const payload = action.payload;
      // DEV-3710: live WS `update` frames dispatch the bare mission object,
      // which carries its own `data` field (VDA-actions array, `[]` in
      // practice) — that truthy array must NOT shadow the mission, otherwise
      // every live update frame is silently dropped. Discriminate id-first
      // (mirroring missionsPage/reducers.ts): take the payload when it is
      // itself a mission (has `id`); only peel `payload.data` when THAT is a
      // mission object (has `id`). Anything else is dropped by the guard below.
      let source = null;
      if (payload && typeof payload === "object") {
        if ("id" in payload && payload.id) {
          source = payload;
        } else if (
          payload.data &&
          typeof payload.data === "object" &&
          !Array.isArray(payload.data) &&
          payload.data.id
        ) {
          source = payload.data;
        }
      }
      const receivedMission = source ? deepCamelCaseKeys(source) : null;
      if (!receivedMission || !receivedMission.id) return state;

      if (state.isMissionsUpdatesPaused) {
        state.missionsUpdatesStorage.push(receivedMission);
        return state;
      }

      const isNewMission = !state.missionsQueue?.find(
        (mission) => mission?.id === receivedMission?.id
      );

      if (isNewMission) {
        const currentSelectedVehicleIds = state.selectedVehicleIds;

        const noSelectedVehicles = currentSelectedVehicleIds.length === 0;
        // DEV-3710: fleet-unassigned missions (vehicle == null) must always be
        // visible regardless of the active vehicle filter — includes(null) is
        // always false, so guard it explicitly.
        const isUnassigned = receivedMission?.vehicle == null;
        const newMissionIsPartOfSelectedVehicles =
          currentSelectedVehicleIds.includes(receivedMission?.vehicle);

        const vehicleMatches =
          noSelectedVehicles ||
          isUnassigned ||
          newMissionIsPartOfSelectedVehicles;

        // DEV-3710: also gate on the active status tab so a live mission isn't
        // injected into the Completed list (or a finished one into Deployed)
        // between refetches.
        if (
          vehicleMatches &&
          matchesActiveTab(receivedMission?.status, state.activeTab)
        ) {
          // DEV-1629: preserve OVM queue ordering (displayAt-aware) when
          // injecting the live mission.
          state.missionsQueue = sortMissionsByDeploymentDate([
            receivedMission,
            ...(state.missionsQueue?.length ? state.missionsQueue : []),
          ]);
          // A mission newly appeared in this tab's view => fleet counts moved.
          state.countsRefreshToken += 1;
        }
      } else {
        // DEV-3710: a status change moves the mission between tab buckets
        // (e.g. IN_PROGRESS -> FINISHED: Deployed-- / Completed++), so refresh
        // the badges. A pure field tick (passedStops/passedLoops, same status)
        // does NOT change any count, so it is intentionally skipped to avoid a
        // refetch on every loop/stop progress frame.
        const prevStatus = state.missionsQueue?.find(
          (mission) => mission.id === receivedMission?.id
        )?.status;
        if (prevStatus !== receivedMission?.status) {
          state.countsRefreshToken += 1;
        }
        // DEV-3710: merge the WS fields, then drop the just-updated mission from
        // the list if its new status no longer matches the active tab (e.g.
        // IN_PROGRESS -> FINISHED removes it from the Deployed tab). Other rows
        // are left untouched — we only react to the mission this frame carries.
        state.missionsQueue =
          state.missionsQueue
            ?.map((mission) =>
              mission.id === receivedMission?.id
                ? {
                    ...mission,
                    vehicle: receivedMission?.vehicle,
                    status: receivedMission?.status,
                    passedLoops: receivedMission?.passedLoops,
                    passedStops: receivedMission?.passedStops,
                    stops: receivedMission?.stops,
                  }
                : mission
            )
            .filter(
              (mission) =>
                mission.id !== receivedMission?.id ||
                matchesActiveTab(mission.status, state.activeTab)
            ) ?? [];
      }
      return state;
    },
    setSelectedVehicleIds: (state, action) => {
      state.selectedVehicleIds = action.payload.selected
        ? [...state.selectedVehicleIds, action.payload.vehicleId]
        : state.selectedVehicleIds.filter(
            (selectedVehicleId) =>
              selectedVehicleId !== action.payload.vehicleId
          );
      return state;
    },
    resetPagination: (state) => {
      state.limit = 20;
      return state;
    },
    pauseMissionsUpdates: (state) => {
      state.isMissionsUpdatesPaused = true;
      return state;
    },
    continueMissionsUpdates: (state) => {
      state.isMissionsUpdatesPaused = false;
      return state;
    },
    resetMissionsUpdatesStorage: (state) => {
      state.missionsUpdatesStorage = [];
      return state;
    },
  },
});

export default Slice;
